
import { QueueExecutionArgs } from "@/shared/Executions/models/QueueExecutionArgs";
import { IQueueAdvancedAnalysisExecutionRequest } from "@/shared/Executions/models/IQueueExecutionRequest";
import { AdvancedAnalysis } from "../models/AdvancedAnalysis";
import { AdvancedAnalysisSchema } from "../models/AdvancedAnalysisSchema";
import { AdvancedAnalysisField } from "../models/AdvancedAnalysisField";
import { AdvancedAnalysisFieldType } from "../models/AdvancedAnalysisFieldType";
import { AdvancedAnalysisTypeGuardService } from "./AdvancedAnalysisTypeGuardService";
import { JsonEditorSchemaType } from "@/shared/JsonEditorSchema/models/JsonEditorCdnSchemaType";
import { QueueDataCleaningArgs } from "@/shared/Executions/models/QueueDataCleaningArgs";
import { QueueNCAAnalysisArgs } from "@/shared/Executions/models/QueueNCAAnalysisArgs";
import { QueueCustomCalculationsArgs } from "@/shared/Executions/models/QueueCustomCalculationsArgs";
import { QueuePlotsArgs } from "@/shared/Executions/models/QueuePlotsArgs";
import { QueueTablesArgs } from "@/shared/Executions/models/QueueTablesArgs";
import { QueueCustomReportsArgs } from "@/shared/Executions/models/QueueCustomReportsArgs";
import { QueueExecutionRequestFactory } from "@/shared/Executions/services/QueueExecutionRequestFactory";

export class AdvancedAnalysisService {

    public static readonly CUSTOM_ADVANCED_ANALYSIS_TYPE = "unknown";

    /// Advanced Analyses have abitrary keys as their typing is relatively loose.
    /// This method will reduce/flatten the output to include only what is expected
    /// in Advanced Analysis processing.
    public static getFlattenedAdvancedAnalysis(advancedAnalysis: AdvancedAnalysis, includeType: string | null): AdvancedAnalysis {
        if (advancedAnalysis == null) throw new Error("advancedAnalysis is required");

        if (advancedAnalysis.schema?.payload_template != null) {
            const payload_template = advancedAnalysis.schema.payload_template;
            return AdvancedAnalysisService.hydratePayloadTemplate(payload_template, advancedAnalysis.configuration || {});
        }
        else {
            const advancedAnalysisObject = advancedAnalysis.configuration ?? advancedAnalysis;
            const { _type, ...properties } = advancedAnalysisObject || {};
            const flattenedAdvancedAnalysis = { ...properties } as AdvancedAnalysis;
            // Only include type data if at the top-level or configuration is a a known type
            if (includeType) {
                flattenedAdvancedAnalysis.type = includeType
            }
            return flattenedAdvancedAnalysis;
        }

    }

    public static createAdvancedAnalysisFromAdvancedAnalysisSchema(advancedAnalysis: AdvancedAnalysisSchema): AdvancedAnalysis {

        const newAdvancedAnalysis: AdvancedAnalysis = {
            id: this.generateAdvancedAnalysisId(),
            _type: advancedAnalysis.type,
            display_name: advancedAnalysis.display_name,
            description: 'New Advanced Analysis configuration',
            category: advancedAnalysis.category,
            configuration: {},
            schema: advancedAnalysis
        }

        return newAdvancedAnalysis;

    }

    public static createAdvancedAnalysisFromAdvancedAnalysisField(advancedAnalysisField: AdvancedAnalysisField): AdvancedAnalysis {

        const newAdvancedAnalysis: AdvancedAnalysis = {
            id: this.generateAdvancedAnalysisId(),
            _type: advancedAnalysisField.field_type,
            display_name: advancedAnalysisField.display_name,
            display_field: advancedAnalysisField.display_field,
            description: advancedAnalysisField.description,
            category: "Default Value",
            configuration: {},
            schema: advancedAnalysisField
        }

        return newAdvancedAnalysis;

    }

    private static generateAdvancedAnalysisId(): string {
        const id = `advanced-analysis--${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
        return id;
    }

    public static initializeAdvancedAnalysisConfiguration(advancedAnalysis: AdvancedAnalysis, fields: Array<AdvancedAnalysisField>): Record<string, any> {
        const config: Record<string, any> = {}
        config.description = `${advancedAnalysis.display_name ?? "Advanced Analysis"} Description`
        fields.forEach((field: AdvancedAnalysisField) => {
            this.initializeAdvancedAnalysisConfigurationForField(field, config);
        })
        config._type = advancedAnalysis._type
        return config;
    }

    public static initializeAdvancedAnalysisFieldConfiguration(fields: Array<AdvancedAnalysisField>): Record<string, any> {
        const config: Record<string, any> = {}
        //config.description = `${rule.display_name ?? "Rule"} Description`
        fields.forEach((field: AdvancedAnalysisField) => {
            this.initializeAdvancedAnalysisConfigurationForField(field, config);
        })
        return config;
    }

    public static initializeAdvancedAnalysisConfigurationForField(field: AdvancedAnalysisField, configuration?: Record<string, any>): Record<string, any> {
        const config: Record<string, any> = configuration || {};
        if (field.default_value !== undefined) {
            config[field.name] = field.default_value
        } else if (field.field_type === AdvancedAnalysisFieldType.Boolean) {
            config[field.name] = false
        } else if (
            field.field_type === AdvancedAnalysisFieldType.MultiSelect ||
            field.field_type === AdvancedAnalysisFieldType.Array ||
            field.field_type === AdvancedAnalysisFieldType.ArrayValues
        ) {
            config[field.name] = []
        } else if (
            field.field_type === AdvancedAnalysisFieldType.Object ||
            field.field_type === JsonEditorSchemaType.RuleSchema ||
            field.field_type === JsonEditorSchemaType.AnalysisConfigSchema ||
            field.field_type === JsonEditorSchemaType.CalculationSchema ||
            field.field_type === JsonEditorSchemaType.PlotSchema ||
            field.field_type === JsonEditorSchemaType.TableSchema ||
            field.field_type === JsonEditorSchemaType.CustomReportSchema
        ) {
            config[field.name] = {}
        } else {
            config[field.name] = ''
        }
        return config;
    }

    public static hydratePayloadTemplate(
        template: any,
        context: Record<string, any>
    ): any {

        if (typeof template === 'string') {
            const match = template.match(/^<([^<>]+)>$/);
            if (match) {
                const key = match[1];
                return context.hasOwnProperty(key) ? context[key] : template;
            }
            return template;
        }

        if (Array.isArray(template)) {
            return template.map(item => AdvancedAnalysisService.hydratePayloadTemplate(item, context));
        }

        if (template && typeof template === 'object') {
            const result: any = {};
            for (const [key, value] of Object.entries(template)) {
                result[key] = AdvancedAnalysisService.hydratePayloadTemplate(value, context);
            }
            return result;
        }

        return template; // return as-is for primitives (number, boolean, null, etc.)
    }

    public static createQueueAdvancedAnalysisArgs(
        userId: string,
        tenantId: string,
        analysisFileId: string,
        advancedAnalyses: Array<AdvancedAnalysis>,
        name?: string): QueueExecutionArgs {

        if (userId == null) throw Error("userId is required!");
        if (tenantId == null) throw Error("tenantId is required!");
        if (analysisFileId == null) throw Error("analysisFileId is required!");
        if (!advancedAnalyses?.length) throw Error("advancedAnalyses are required!");

        // Only use first config, even though standalone designer technically supports an array of configs
        let flattenedAdvancedAnalysis: AdvancedAnalysis | any = {};
        if (advancedAnalyses?.length) {
            flattenedAdvancedAnalysis = advancedAnalyses[0];
        }

        const dataCleaningRules = flattenedAdvancedAnalysis?.dataCleaning?.rules;
        const dataCleaningOutputFileName = flattenedAdvancedAnalysis?.dataCleaning?.output?.fileName;
        const ncaAnalysisConfig = flattenedAdvancedAnalysis.calculation;
        const customCalculations = flattenedAdvancedAnalysis.customCalculations?.calculations;
        const customCalculationSort = flattenedAdvancedAnalysis.customCalculations?.output?.sort;
        const customPlots = flattenedAdvancedAnalysis.customPlots?.plots;
        const customTables = flattenedAdvancedAnalysis.customTables?.tables;
        const customReports = flattenedAdvancedAnalysis.customReports?.reports;

        const args = new QueueExecutionArgs({
            tenantId,
            userId,
            dataCleaningArgs: Array.isArray(dataCleaningRules) ? new QueueDataCleaningArgs(dataCleaningRules, undefined, dataCleaningOutputFileName) : undefined,
            ncaAnalysisArgs: new QueueNCAAnalysisArgs(ncaAnalysisConfig),
            customCalculationsArgs: Array.isArray(customCalculations) ? new QueueCustomCalculationsArgs(customCalculations, customCalculationSort) : undefined,
            plotArgs: Array.isArray(customPlots) ? new QueuePlotsArgs(customPlots) : undefined,
            tableArgs: Array.isArray(customTables) ? new QueueTablesArgs(customTables) : undefined,
            customReportArgs: Array.isArray(customReports) ? new QueueCustomReportsArgs(customReports) : undefined,
        })

        args.anyalsisFileId = analysisFileId;
        args.name = name ?? flattenedAdvancedAnalysis.name;

        return args
    }

    public static getFormattedAdvancedAnalysisJson(anyalsisFileId: string, advancedAnalyses: Array<AdvancedAnalysis>): IQueueAdvancedAnalysisExecutionRequest {

        const flattenedAdvancedAnalyses = this.getFlattendAdvancedAnalyses(advancedAnalyses);

        const args = AdvancedAnalysisService.createQueueAdvancedAnalysisArgs("", "", anyalsisFileId, flattenedAdvancedAnalyses);
        const payload = QueueExecutionRequestFactory.createQueueExecutionRequest(args) as IQueueAdvancedAnalysisExecutionRequest;

        payload.type = flattenedAdvancedAnalyses?.length > 0 ? flattenedAdvancedAnalyses[0].type : undefined;

        return payload;
    }

    public static getFlattendAdvancedAnalyses(advancedAnalyses: Array<AdvancedAnalysis>): Array<AdvancedAnalysis> {

        const flattenedAdvancedAnalyses = (advancedAnalyses || []).map((x) => AdvancedAnalysisService.getFlattenedAdvancedAnalysis(x, x._type));
        this.recursivelyFlattenAdvancedAnalyses(flattenedAdvancedAnalyses);
        return flattenedAdvancedAnalyses;

    }

    private static recursivelyFlattenAdvancedAnalyses(advancedAnalyses: Array<AdvancedAnalysis | Object>, visited = new Set<any>()) {
        for (const advancedAnalysis of advancedAnalyses) {
            if (visited.has(advancedAnalysis)) continue;
            visited.add(advancedAnalysis);

            for (const key of Object.keys(advancedAnalysis)) {
                const value = (advancedAnalysis as any)[key];

                // Case: Direct unflattened entity property (e.g. calculation) — has _type + configuration wrapper
                if (AdvancedAnalysisTypeGuardService.isUnflattenedAdvancedAnalysis(value)) {
                    const includeType = !this.isInternalSchemaType(value._type) ? value._type : null;
                    const flattened = this.getFlattenedAdvancedAnalysis(value, includeType);
                    (advancedAnalysis as any)[key] = flattened;
                    this.recursivelyFlattenAdvancedAnalyses([flattened], visited);
                }
                else if (Array.isArray(value)) {
                    const containsSubAdvancedAnalyses =
                        value.length > 0 &&
                        value.every((item) => item && AdvancedAnalysisTypeGuardService.isAdvancedAnalaysis(item));

                    if (containsSubAdvancedAnalyses) {
                        const flattnedSubAdvancedAnalyses = value.map((subAdvancedAnalysis: AdvancedAnalysis) => {
                            const includeType = !this.isInternalSchemaType(subAdvancedAnalysis._type) ? subAdvancedAnalysis._type : null;
                            return this.getFlattenedAdvancedAnalysis(subAdvancedAnalysis, includeType);
                        });

                        (advancedAnalysis as any)[key] = flattnedSubAdvancedAnalyses;
                        this.recursivelyFlattenAdvancedAnalyses(flattnedSubAdvancedAnalyses, visited);
                    }
                }
                else if (AdvancedAnalysisTypeGuardService.isNonEmptyAdvancedAnalysisObject(value)) {

                    for (const nestedKey of Object.keys(value)) {
                        const nestedValue = value[nestedKey];

                        // Case 1: Direct advanced analysis object
                        if (AdvancedAnalysisTypeGuardService.isAdvancedAnalaysis(nestedValue)) {
                            const includeType = !this.isInternalSchemaType(nestedValue._type) ? nestedValue._type : null;
                            const flattened = this.getFlattenedAdvancedAnalysis(nestedValue, includeType);
                            value[nestedKey] = flattened;

                            this.recursivelyFlattenAdvancedAnalyses([flattened], visited);
                        }

                        // Case 2: Another nested non-empty advanced analysis-like object
                        else if (AdvancedAnalysisTypeGuardService.isNonEmptyAdvancedAnalysisObject(nestedValue)) {
                            const coerced = nestedValue as Object;
                            this.recursivelyFlattenAdvancedAnalyses([coerced], visited);
                        }

                        else if (Array.isArray(nestedValue)) {
                            const advancedAnalysesArr = this.extractAdvancedAnalysesFromArray(nestedValue);
                            if (advancedAnalysesArr.length > 0) {
                                const flattened = advancedAnalysesArr.map(subAdvancedAnalysis => {
                                    const includeType = !this.isInternalSchemaType(subAdvancedAnalysis._type) ? subAdvancedAnalysis._type : null;
                                    return this.getFlattenedAdvancedAnalysis(subAdvancedAnalysis, includeType);
                                });
                                value[nestedKey] = flattened;
                                this.recursivelyFlattenAdvancedAnalyses(flattened, visited);
                            }
                        }
                    }
                }

            }
        }
    }

    private static extractAdvancedAnalysesFromArray(arr: unknown[]): AdvancedAnalysis[] {
        return arr.filter(AdvancedAnalysisTypeGuardService.isAdvancedAnalaysis);
    }

    public static getFormattedAdvancedAnalysisJsonString(anyalsisFileId: string, advancedAnalyses: Array<AdvancedAnalysis>): string {
        const payload = AdvancedAnalysisService.getFormattedAdvancedAnalysisJson(anyalsisFileId, advancedAnalyses)

        return JSON.stringify(payload, null, 2)
    }

    public static tryGetAdvancedAnalysisSchemaByType(type: string, advancedAnalysesSchemas: Array<AdvancedAnalysisSchema>): AdvancedAnalysisSchema | undefined {

        if (type == null) throw new Error("type is required");
        if (advancedAnalysesSchemas == null) throw new Error("advancedAnalysesSchemas are required");

        return advancedAnalysesSchemas.find(x => x.type === type) || advancedAnalysesSchemas.find(x => x.type === AdvancedAnalysisService.CUSTOM_ADVANCED_ANALYSIS_TYPE);

    }

    // Certain item_scheam structures use types like 'object' or 'array' that we do not want
    // persisted in output JSON.
    public static isInternalSchemaType(type: string): boolean {

        const internalTypes = ['object', 'array'];
        return internalTypes.indexOf(type?.toLowerCase().trim()) !== -1;

    }


}


